import { Placement } from "@floating-ui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import get from "lodash/get";
import { ReactElement } from "react";
import {
  FieldErrors,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormRegister,
} from "react-hook-form";

import { Description } from "../catalyst/Description";
import { ErrorMessage } from "../catalyst/ErrorMessage";
import { Field } from "../catalyst/Field";
import { Input } from "../catalyst/Input";
import { Label } from "../catalyst/Label";
import { Textarea } from "../catalyst/Textarea";
import { Tooltip } from "../Tooltip";

export interface InputFieldGroupProps<FormType extends FieldValues> {
  description?: string;
  disabled?: boolean;
  errors: FieldErrors<FormType>;
  fieldName: Path<FormType>;
  label: ReactElement | string;
  multiline?: boolean;
  placeHolder: string;
  register: UseFormRegister<FormType>;
  rows?: number;
  rules?: RegisterOptions<FormType, Path<FormType>>;
  tooltip?: {
    position: Placement;
    text: string;
  };
  type?: string;
}

const InputFieldGroup = <FormType extends FieldValues>({
  description,
  disabled = false,
  errors,
  fieldName,
  label,
  multiline = false,
  placeHolder,
  register,
  rows,
  rules,
  tooltip,
  type = "text",
}: InputFieldGroupProps<FormType>): ReactElement => {
  const errorObject = get(errors, fieldName);
  const errorMessage =
    typeof errorObject?.message === "string" ? errorObject.message : undefined;
  return (
    <div className="flex flex-col gap-2">
      <Field>
        {typeof label === "string" ? (
          <Label className="flex items-center">
            {label}
            {tooltip && (
              <span className="ml-1">
                <Tooltip content={tooltip.text} position={tooltip.position}>
                  <InformationCircleIcon className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                </Tooltip>
              </span>
            )}
          </Label>
        ) : (
          label
        )}
        {multiline ? (
          <Textarea
            {...register(fieldName, rules)}
            disabled={disabled}
            invalid={errorMessage != null && errorMessage != undefined}
            placeholder={placeHolder}
            rows={rows}
          />
        ) : (
          <Input
            type={type}
            {...register(fieldName, rules)}
            disabled={disabled}
            invalid={errorMessage != null && errorMessage != undefined}
            placeholder={placeHolder}
          />
        )}
        {description && <Description>{description}</Description>}
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </Field>
    </div>
  );
};

export default InputFieldGroup;
